import { useRef, useState } from "react";
import { Hourglass } from "lucide-react";
import { LIBRARY_FONT_STEPS, stepLibraryFont } from "../utils/libraryReading";
import MenuIconTooltip from "./MenuIconTooltip";
import LibraryReadingPreferencesModal from "./LibraryReadingPreferencesModal";
import "./LibraryProgress.css";

function pageFromPointer(event, track, total) {
  const rect = track.getBoundingClientRect();
  if (!rect.width || total < 1) return 1;
  const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
  return Math.min(total, Math.max(1, Math.round(ratio * (total - 1)) + 1));
}

export function LibraryFontSize({ fontSize, onChange, pending = false }) {
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const atMin = fontSize <= LIBRARY_FONT_STEPS[0];
  const atMax = fontSize >= LIBRARY_FONT_STEPS[LIBRARY_FONT_STEPS.length - 1];
  return (
    <div className="library-font">
      <MenuIconTooltip
        tooltip="Minuteur de lecture"
        placement="below"
        portal
        className="library-font__gear-tip"
      >
        <button
          type="button"
          className="library-font__button library-font__gear"
          aria-label="Minuteur de lecture"
          onClick={() => setPreferencesOpen(true)}
        >
          <Hourglass size={21} strokeWidth={2} aria-hidden="true" />
        </button>
      </MenuIconTooltip>
      {pending ? (
        <>
          <span className="register-skel library-font-skel__button" />
          <span className="register-skel library-font-skel__button" />
        </>
      ) : (
        <>
          <button
            type="button"
            className="library-font__button"
            disabled={atMin}
            aria-label="Diminuer la taille du texte"
            onClick={() => onChange(stepLibraryFont(fontSize, -1))}
          >
            A−
          </button>
          <button
            type="button"
            className="library-font__button library-font__button--plus"
            disabled={atMax}
            aria-label="Augmenter la taille du texte"
            onClick={() => onChange(stepLibraryFont(fontSize, 1))}
          >
            A+
          </button>
        </>
      )}
      <LibraryReadingPreferencesModal
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
    </div>
  );
}

export default function LibraryProgress({ page = 1, total = 1, pending = false, onSeek = () => {} }) {
  const trackRef = useRef(null);
  const [dragPage, setDragPage] = useState(null);
  const shown = dragPage == null ? page : dragPage;
  const ratio = total > 1 && shown >= 1 ? (shown - 1) / (total - 1) : 0;

  const seekFromEvent = (event) => {
    const next = pageFromPointer(event, trackRef.current, total);
    setDragPage(next);
    onSeek(next);
  };

  if (pending) {
    return (
      <div className="library-progress library-progress--pending" aria-hidden="true">
        <span className="library-progress__num">
          <span className="register-skel library-progress-skel__num" />
        </span>
        <div className="library-progress__track">
          <span className="register-skel library-progress-skel__line" />
        </div>
        <span className="library-progress__num" data-library-page-total="">
          <span className="register-skel library-progress-skel__num" />
        </span>
      </div>
    );
  }

  return (
    <div className={`library-progress${dragPage == null ? "" : " library-progress--drag"}`}>
      <span className="library-progress__num">{shown >= 1 ? shown : "—"}</span>
      <div
        ref={trackRef}
        className="library-progress__track"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          seekFromEvent(event);
        }}
        onPointerMove={(event) => {
          if (dragPage == null) return;
          seekFromEvent(event);
        }}
        onPointerUp={() => setDragPage(null)}
        onPointerCancel={() => setDragPage(null)}
      >
        <span className="library-progress__fill" style={{ width: `${ratio * 100}%` }} />
        <span className="library-progress__thumb" style={{ left: `${ratio * 100}%` }} />
      </div>
      <span className="library-progress__num" data-library-page-total="">
        {total || "—"}
      </span>
    </div>
  );
}
